import React, { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';

/**
 * Portal auth context — Service Provider session management.
 *
 * Parallel to the staff useAuth() in lib/auth.tsx but completely separate:
 * - Different cookie (katara_portal_session)
 * - Different endpoints (/api/portal/auth/*)
 * - Different user shape (Profile-derived, not User-derived)
 *
 * Used only by components inside the portal route (/portal/*).
 */

export interface PortalUser {
  id: string;
  workingName: string;
  email: string | null;
  phone: string | null;
  assignedPropertyId: string | null;
  assignedProperty: { id: string; name: string } | null;
  mustChangePassword: boolean;
  notifyVia: 'SMS' | 'EMAIL' | 'BOTH';
}

// --- Direct fetch helpers (don't reuse staff api.ts to keep separation clean) ---

async function portalFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.error || `HTTP ${res.status}`);
  }
  return json as T;
}

export const portalApi = {
  me: () => portalFetch<PortalUser>('/api/portal/me'),

  login: (username: string, password: string) =>
    portalFetch<{ profile: PortalUser }>('/api/portal/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    }),

  logout: () =>
    portalFetch<{ ok: boolean }>('/api/portal/auth/logout', { method: 'POST' }),

  changePassword: (currentPassword: string, newPassword: string) =>
    portalFetch<{ ok: boolean }>('/api/portal/auth/change-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
};

// --- React context ---

interface PortalAuthContextValue {
  user: PortalUser | null;
  loading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  clearError: () => void;
}

const PortalAuthContext = createContext<PortalAuthContextValue | null>(null);

export function PortalAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const u = await portalApi.me();
      setUser(u);
    } catch (e) {
      // Not authenticated — that's fine, just no user
      setUser(null);
    }
  }, []);

  // On mount, check for existing session
  useEffect(() => {
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const login = useCallback(async (username: string, password: string) => {
    setError(null);
    try {
      const res = await portalApi.login(username, password);
      setUser(res.profile);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      throw e;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await portalApi.logout();
    } catch {
      // Even if logout fails server-side, clear local state
    }
    setUser(null);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return (
    <PortalAuthContext.Provider value={{ user, loading, error, login, logout, refresh, clearError }}>
      {children}
    </PortalAuthContext.Provider>
  );
}

export function usePortalAuth(): PortalAuthContextValue {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) {
    throw new Error('usePortalAuth must be used inside <PortalAuthProvider>');
  }
  return ctx;
}
